// IoT Dashboard Page
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Grid, Paper, TextField, MenuItem } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DeviceCard from '../components/DeviceCard';
import { useIotDevices } from '../hooks/useIotDevices';
import { iotApiService } from '../services/iotApiService';
import { DOOR_MAPPINGS, resolveDoorMapping } from '../doorMappings';
import ccmDoorLayoutSvg from '../assets/ccm-door-layout.svg';

const IotDashboardPage: React.FC = () => {
  const [isInitializing, setIsInitializing] = useState(true);
  const { data: devices, isLoading, error, refetch } = useIotDevices(true);

  const [doorIdInput, setDoorIdInput] = useState('');
  const [openDurationInput, setOpenDurationInput] = useState('5');
  const [isDoorActionLoading, setIsDoorActionLoading] = useState(false);
  const [doorActionError, setDoorActionError] = useState<string | null>(null);
  const [doorActionSuccess, setDoorActionSuccess] = useState<string | null>(null);

  useEffect(() => {
    // Auto-initialize authentication on mount
    const initialize = async () => {
      try {
        await iotApiService.ensureAuthenticated();
      } catch (error) {
        console.error('Failed to initialize IoT authentication:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initialize();
  }, []);

  // Listen for main app logout events
  useEffect(() => {
    const handleMainAppLogout = () => {
      console.log('🧹 Cleaning up IoT tokens due to main app logout');
      iotApiService.logout();
    };

    window.addEventListener('authExpired', handleMainAppLogout);
    window.addEventListener('beforeunload', handleMainAppLogout);

    return () => {
      window.removeEventListener('authExpired', handleMainAppLogout);
      window.removeEventListener('beforeunload', handleMainAppLogout);
    };
  }, []);

  const handleRefresh = () => {
    refetch();
  };

  const parsedDoorId = Number(doorIdInput);
  const parsedOpenDuration = Number(openDurationInput);
  const resolved = Number.isFinite(parsedDoorId) ? resolveDoorMapping(parsedDoorId) : null;

  const sendDoorCommand = async (action: 0 | 1 | 2 | 3) => {
    setDoorActionError(null);
    setDoorActionSuccess(null);

    if (!resolved) {
      setDoorActionError('Door is not mapped. Update DOOR_MAPPINGS in src/modules/iot/doorMappings.ts.');
      return;
    }

    const time = action === 3 ? parsedOpenDuration : 0;
    if (action === 3 && (!Number.isFinite(time) || time <= 0)) {
      setDoorActionError('Open duration must be a positive number (seconds).');
      return;
    }

    setIsDoorActionLoading(true);
    try {
      const response = await iotApiService.controlDoor({
        action,
        sn: resolved.sn,
        door: resolved.door,
        time,
      });

      if (response.code === 0) {
        const label = resolved.label ? ` (${resolved.label})` : '';
        setDoorActionSuccess(`Command sent: action=${action}, doorId=${resolved.doorId}${label}`);
      } else {
        setDoorActionError(response.msg || 'Door command failed');
      }
    } catch (err) {
      setDoorActionError(err instanceof Error ? err.message : 'Door command failed');
    } finally {
      setIsDoorActionLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Calculate statistics
  const totalDevices = devices?.length || 0;
  const onlineDevices = devices?.filter(d => d.status === 1).length || 0;
  const offlineDevices = totalDevices - onlineDevices;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          IoT Device Dashboard
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isLoading}>
          Refresh
        </Button>
      </Box>

      {/* Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Devices
            </Typography>
            <Typography variant="h4">{totalDevices}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Online Devices
            </Typography>
            <Typography variant="h4" color="success.main">
              {onlineDevices}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Offline Devices
            </Typography>
            <Typography variant="h4" color="error.main">
              {offlineDevices}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load devices. Please try refreshing or login again.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      )}

      {/* Devices Grid */}
      {!isLoading && devices && devices.length > 0 && (
        <Grid container spacing={2}>
          {devices.map(device => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={device.id}>
              <DeviceCard device={device} />
            </Grid>
          ))}
        </Grid>
      )}

      {!isLoading && devices && devices.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Door Control
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a door, then open/close/reset. Door id is resolved to (serial number + door) via a hardcoded mapping.
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label="Door"
                value={doorIdInput}
                onChange={e => setDoorIdInput(e.target.value)}
                fullWidth
                select
                disabled={isDoorActionLoading}
                helperText="Door ID + label"
              >
                <MenuItem value="">
                  <em>Select a door…</em>
                </MenuItem>
                {Object.values(DOOR_MAPPINGS)
                  .slice()
                  .sort((a, b) => a.doorId - b.doorId)
                  .map(mapping => (
                    <MenuItem key={mapping.doorId} value={String(mapping.doorId)}>
                      Door {mapping.doorId}
                      {mapping.label ? ` — ${mapping.label}` : ''}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField
                label="Open duration (sec)"
                value={openDurationInput}
                onChange={e => setOpenDurationInput(e.target.value)}
                fullWidth
                inputProps={{ inputMode: 'numeric' }}
                disabled={isDoorActionLoading}
                helperText="Used for timed open (action=3)"
              />
            </Grid>

            <Grid
              size={{ xs: 12, sm: 4, md: 6 }}
              sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
            >
              <Button variant="contained" onClick={() => void sendDoorCommand(3)} disabled={isDoorActionLoading}>
                Open (Timed)
              </Button>
              <Button variant="outlined" onClick={() => void sendDoorCommand(0)} disabled={isDoorActionLoading}>
                Open (Indefinite)
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => void sendDoorCommand(1)}
                disabled={isDoorActionLoading}
              >
                Close (Indefinite)
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => void sendDoorCommand(2)}
                disabled={isDoorActionLoading}
              >
                Reset Door
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Resolved:{' '}
              {resolved ? `${resolved.sn} (door ${resolved.door})${resolved.label ? ` — ${resolved.label}` : ''}` : '—'}
            </Typography>
          </Box>

          {doorActionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {doorActionError}
            </Alert>
          )}
          {doorActionSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {doorActionSuccess}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Museum IoT Reference Door Layout
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={ccmDoorLayoutSvg}
                alt="CCM door layout"
                sx={{ width: '100%', maxWidth: 1000, display: 'block' }}
              />
            </Box>
          </Box>
        </Paper>
      )}
      {/* No Devices */}
      {!isLoading && devices && devices.length === 0 && <Alert severity="info">No devices found</Alert>}
    </Box>
  );
};

export default IotDashboardPage;
